// Las acciones de una celda que ya están parametrizadas por fila — camino A de
// ADR-2026-09-16-las-acciones-del-lienzo-son-las-que-ya-reciben-una-fila. Lo que necesita
// el estado del inline (Mejorar resolución, Expansión, Recortar, Marca de agua,
// Descripción) se llega por "Ver", que presta el visor del historial; Carrusel y Animar
// abren ese mismo visor con la acción ya disparada
// (ADR-2026-09-16-la-barra-del-lienzo-muestra-carrusel-y-animar).

import type { NavigateFunction } from 'react-router-dom';
import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import type { DatosCelda } from '../../pages/v2/components/useDatosDeCeldas';
import { conVolverA } from '../../utils/volverA';
import { adjuntoDeGenerada } from '../ui/bibliotecaDeAdjuntos';
import { RUTA_CHAT_ATLAS } from '../ui/rutaChatAtlas';

/** A dónde vuelven las pantallas que se abren desde el lienzo: al Agente
 *  (ADR-2026-09-16-volver-a-donde-vine). */
const VOLVER_AL_AGENTE = { path: RUTA_CHAT_ATLAS };

export interface CeldaParaAccion {
  imageId: string;
  companyId: string;
  esVideo: boolean;
  /** Miniatura ya firmada (la que la celda dibuja). */
  src: string;
  datos: DatosCelda;
  /** Título de la tanda: nombre del adjunto de un video. */
  titulo: string;
}

/** El chip para el composer: imagen generada (`gen-<id>`) o video con su path. */
/** Los segundos que se pidieron para un video, leídos de su `datos_generacion` (objeto o
 *  JSON en texto). 0 si no están. */
export function duracionPedida(datosGeneracion: unknown): number {
  let d: unknown = datosGeneracion;
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d);
    } catch {
      return 0;
    }
  }
  const n = d && typeof d === 'object' ? Number((d as Record<string, unknown>).duration) : NaN;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function adjuntoDeCelda(c: CeldaParaAccion): ChatAttachment | null {
  if (!c.datos.fileName) return null;
  const storagePath = `${c.companyId}/full/${c.datos.fileName}`;
  if (c.esVideo) {
    return {
      kind: 'video',
      id: `gen-${c.imageId}`,
      name: c.titulo || 'Video generado',
      storagePath,
      bucket: 'generadas',
      bytes: 0,
      // Lo que se pidió que durara (`duration` del pedido guardado en la fila), si está:
      // quien lo use de referencia necesita saberlo para respetar el tope del proveedor
      // (ADR-2026-10-05-bloqueos-del-video-de-referencia). 0 = no se sabe.
      duracionSeg: duracionPedida(c.datos.datosGeneracion),
      // La miniatura de la celda (el PNG del video): para dibujarlo como referencia.
      ...(c.src ? { previewUrl: c.src } : {}),
    };
  }
  return adjuntoDeGenerada({
    id: c.imageId,
    storagePath,
    previewUrl: c.src,
    bucket: 'generadas',
  });
}

export function irAEdicionCanva(navigate: NavigateFunction, c: CeldaParaAccion): void {
  navigate(`/redes-sociales/edicion-grafica?id=${c.imageId}&companyId=${c.companyId}`, {
    state: conVolverA({ resultsImageIds: [c.imageId], resultsCompanyId: c.companyId }, VOLVER_AL_AGENTE),
  });
}

/**
 * Cuántas de las celdas seleccionadas son ACCIONABLES — lo que decide si una celda
 * muestra su barra (ADR-2026-09-17-la-barra-cuenta-lo-accionable).
 *
 * `seleccionadas` (las `CeldaParaAccion`) sólo cuenta las celdas CON fila, así que una
 * imagen propia del modo Diseño (`imageId: null` y `url`) nunca sumaba: su barra no
 * aparecía jamás, y encima aparecía de más cuando la única seleccionada era otra celda
 * con fila. Acá cuentan las dos: una celda de fila cuando su fila ya llegó (`tieneFila`),
 * y una propia sólo si hay una barra prestada que sepa qué hacer con ella.
 *
 * Sin barra prestada el resultado es exactamente `seleccionadas.length`: el lienzo del
 * Agente se comporta igual que antes. Pura a propósito: se prueba sin React ni red.
 */
export function contarAccionables(
  nodos: { id: string; data: { imageId: string | null; url?: string } }[],
  seleccionIds: readonly string[],
  tieneFila: (imageId: string) => boolean,
  conBarraPrestada: boolean,
): number {
  const seleccion = new Set(seleccionIds);
  let total = 0;
  for (const nodo of nodos) {
    if (!seleccion.has(nodo.id)) continue;
    const { imageId, url } = nodo.data;
    if (imageId ? tieneFila(imageId) : !!url && conBarraPrestada) total += 1;
  }
  return total;
}
